import React, { useEffect, useRef, useState } from "react";
import { Accessibility, Check, Moon, Sun } from "lucide-react";
import { useTheme } from "../utils/theme";

export default function AccessibilityMenu({ className = "", align = "end" }) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const {
    theme,
    setTheme,
    fontSize,
    increaseFontSize,
    decreaseFontSize,
    resetFontSize,
  } = useTheme();

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const alignmentClass = align === "start" ? "start-0" : "end-0";

  return (
    <div ref={menuRef} className={`position-relative ${className}`.trim()}>
      <button
        type="button"
        className="btn accessibility-trigger"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="Open accessibility settings"
        onClick={() => setIsOpen((currentValue) => !currentValue)}
      >
        <Accessibility size={18} />
      </button>

      {isOpen && (
        <div className={`accessibility-menu dropdown-menu show ${alignmentClass}`}>
          <div className="accessibility-menu-header px-3 py-3 border-bottom">
            <div className="fw-semibold accessibility-title d-flex align-items-center gap-2">
              <span className="accessibility-menu-icon">
                <Accessibility size={15} />
              </span>
              <span className="accessibility-title-text">Accessibility Tools</span>
            </div>
          </div>

          <div className="accessibility-section px-3 py-3">
            <div className="fw-medium accessibility-section-label mb-2">Theme</div>
            <div className="accessibility-segmented">
              <button
                type="button"
                className={`btn btn-sm accessibility-option p-0 ${theme === "light" ? "is-active" : ""}`}
                onClick={() => setTheme("light")}
              >
                <span className="accessibility-option-check">
                  {theme === "light" ? <Check size={14} /> : null}
                </span>
                <div className="accessibility-option-content d-flex align-items-center justify-content-center gap-2">

                  <span className="accessibility-option-icon">
                    <Sun size={18} />
                  </span>
                  <span className="accessibility-option-text">Light</span>
                </div>
              </button>
              <button
                type="button"
                className={`btn btn-sm accessibility-option ${theme === "dark" ? "is-active" : ""}`}
                onClick={() => setTheme("dark")}
              >
                <span className="accessibility-option-check">
                  {theme === "dark" ? <Check size={14} /> : null}
                </span>
                <div className="accessibility-option-content d-flex align-items-center justify-content-center gap-2">
                  <span className="accessibility-option-icon">
                    <Moon size={18} />
                  </span>
                  <span className="accessibility-option-text">Dark</span>
                </div>

              </button>
            </div>
          </div>

          <div className="accessibility-section px-3 pb-3">
            <div className="fw-medium accessibility-section-label mb-2">
              Text Size
            </div>

            <div className="accessibility-size-grid">

              {/* NORMAL */}
              <button
                className={`btn btn-sm accessibility-option accessibility-size-option ${fontSize === 16 ? "is-active" : ""
                  }`}
                onClick={resetFontSize}
              >
                <span className="accessibility-option-check">
                  {fontSize === 16 && <Check size={14} />}
                </span>
                <span className="accessibility-option-text">Normal</span>
              </button>

              {/* INCREASE */}
              <button
                className={`btn btn-sm accessibility-option accessibility-size-option ${fontSize > 16 ? "is-active" : ""
                  }`}
                onClick={increaseFontSize}
              >
                <span className="accessibility-option-check">
                  {fontSize > 16 && <Check size={14} />}
                </span>
                <span className="accessibility-option-icon accessibility-size-mark">
                  A+
                </span>
              </button>

              {/* DECREASE */}
              <button
                className={`btn btn-sm accessibility-option accessibility-size-option ${fontSize < 16 ? "is-active" : ""
                  }`}
                onClick={decreaseFontSize}
              >
                <span className="accessibility-option-check">
                  {fontSize < 16 && <Check size={14} />}
                </span>
                <span className="accessibility-option-icon accessibility-size-mark">
                  A-
                </span>
              </button>

            </div>
          </div>
        </div>
      )
      }
    </div >
  );
}
